"use client"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useCharacterStore } from "@/features/characters/store"
import type { CharacterDraft } from "@/features/characters/store"
import { useCreateCharacter } from "@/features/characters/hooks/mutations/use-character-mutations"
import { useCharacter } from "@/features/characters/hooks/queries/use-characters"
import { useCoins } from "@/features/credits/hooks/use-credits"
import { checkFreeUserLimits } from "@/features/credits/actions"
import { COIN_COSTS, isPaidTier } from "@/features/credits/constants"
import { characterSchema } from "@/features/characters/validations"
import { useAuthStore } from "@/features/auth/store"
import { useUIStore } from "@/shared/stores/ui-store"
import { cn } from "@/shared/lib/utils"
import { motion, AnimatePresence } from "framer-motion"
import { Loader2, Coins } from "lucide-react"
import { Icon } from "@/shared/components/icons"
import { toast } from "sonner"
import { track } from "@/shared/lib/analytics"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"
import { isDevScenarioActive } from "@/shared/lib/dev-simulation"
import {
  buildRandomCharacterDefaults,
  fillMissingRandomDefaults,
  isPristineCharacterDraft,
} from "@/features/create/lib/random-character-defaults"
import {
  clearCreateWizardSession,
  loadCreateWizardSession,
  saveCreateWizardSession,
  shouldRestoreCreateSession,
  type CreateWizardSession,
} from "@/features/create/lib/create-wizard-persistence"
import { getBringToLifeLabel } from "@/features/create/lib/bring-to-life-label"
import {
  CHARACTER_CREATION_MESSAGE_LIMIT_TRIGGER,
  shouldPreGateCharacterCreationForMessageLimit,
} from "@/shared/lib/character-creation-message-limit"
import type { Character } from "@/shared/types/character"
import { useTranslations } from "next-intl"

import { SlideIndicator, SLIDE_COUNT } from "./appearance-step"
import { BasicsStep } from "./basics-step"
import { EthnicityStep } from "./ethnicity-step"
import { HairEyesStep } from "./hair-eyes-step"
import { BodyStep } from "./body-step"
import { PersonalityStep } from "./personality-step"
import { ReviewStep } from "./review-step"

// ─── Defaults for AI-generated fields ────────────────────────────────────────

const CREATION_DEFAULTS = {
  // Used when the (now-optional) description is left blank, so the generation
  // pipeline never receives an empty string to seed the persona with.
  description:
    "A captivating companion with a warm presence and a story that unfolds the more you talk.",
  personality: ["flirty", "caring", "playful"] as string[],
  backstory:
    "A captivating soul whose story is just beginning to unfold. Every conversation reveals a new layer of their intriguing personality.",
  interests: ["conversation", "connection", "adventure"],
  communicationStyle: "casual",
  dominanceLevel: 50,
}

/** Minimum loading time before auth/credit gates (signup, subscribe, buy coins). */
const GENERATION_TEASE_MS = 4500
const CHARACTER_FINALIZE_TIMEOUT_MS = 60_000
// Must match the fallback persisted by the characters Edge Function. The
// fallback means generation is pending, not that the avatar is ready to reveal.
const PENDING_AVATAR_URL = "https://pleasur.ai/images/paywall/generate-default.png"
const PERSONALITY_STEP = 4
const REVIEW_STEP = 5
const STEP_NAMES = ["style", "ethnicity", "hair_eyes", "body", "personality", "review"]

function isCreatedCharacterFinalized(
  character: Character | null | undefined
): character is Character {
  return !!character?.avatar && character.avatar !== PENDING_AVATAR_URL
}

function buildFinalDraft(draft: CharacterDraft) {
  return {
    ...draft,
    gender: draft.gender || "female",
    description:
      draft.description.trim().length >= 10
        ? draft.description.trim()
        : CREATION_DEFAULTS.description,
    personality:
      draft.personality.length > 0
        ? draft.personality
        : CREATION_DEFAULTS.personality,
    backstory: draft.backstory || "",
    interests:
      draft.interests.length > 0
        ? draft.interests
        : CREATION_DEFAULTS.interests,
    communicationStyle:
      draft.communicationStyle || CREATION_DEFAULTS.communicationStyle,
    dominanceLevel: draft.dominanceLevel ?? CREATION_DEFAULTS.dominanceLevel,
  }
}

function waitForGenerationTease() {
  return new Promise<void>((resolve) => setTimeout(resolve, GENERATION_TEASE_MS))
}

interface CreationWizardProps {
  mode?: "live" | "preview"
  initialStep?: number
  initialDraft?: Partial<CharacterDraft>
  initialStatus?: "form" | "creating"
  previewCoinBalance?: number
}

function getSimulatedProgress(elapsedMs: number): number {
  if (elapsedMs < 3000) return (elapsedMs / 3000) * 30
  if (elapsedMs < 11000) return 30 + ((elapsedMs - 3000) / 8000) * 30
  if (elapsedMs < 21000) return 60 + ((elapsedMs - 11000) / 10000) * 25
  if (elapsedMs < 31000) return 85 + ((elapsedMs - 21000) / 10000) * 10
  return 95
}

// ─── Animation variants ─────────────────────────────────────────────────────

// Transform + opacity only — both GPU-composited, so the motion stays at 60fps
// even over the image/video cards. (Animating filter:blur here tanks the frame
// rate because it repaints every frame.)
const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 56 : -56, opacity: 0, scale: 0.96 }),
  center: { x: 0, opacity: 1, scale: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -56 : 56, opacity: 0, scale: 0.96 }),
}

// ─── Step completion (same rules as validateAndNext) ──────────────────────────

function isCurrentStepComplete(
  draft: ReturnType<typeof useCharacterStore.getState>["draft"],
  currentStep: number
): boolean {
  if (currentStep === 0) return !!draft.style
  if (currentStep === 1) return !!draft.ethnicity
  if (currentStep === 2) {
    return !!(draft.eyeColor && draft.hairColor && draft.hairStyle)
  }
  if (currentStep === 3) {
    if (!draft.bodyType) return false
    if (["female", "trans"].includes(draft.gender || "female")) {
      return !!(draft.breastSize && draft.buttSize)
    }
    return true
  }
  // Personality traits are optional here: buildFinalDraft falls back to
  // CREATION_DEFAULTS.personality, which satisfies the server schema.
  if (currentStep === 4) {
    return !!(
      draft.name?.trim() &&
      draft.name.trim().length >= 2 &&
      draft.voiceId
    )
  }
  // Step 5 is Review — no required fields
  return true
}

// ─── Main Wizard ─────────────────────────────────────────────────────────────

export function CreationWizard({
  mode = "live",
  initialStep = 0,
  initialDraft,
  initialStatus = "form",
  previewCoinBalance = 0,
}: CreationWizardProps = {}) {
  const t = useTranslations("create.wizard")
  const creationPhases = useMemo(
    () => [
      t("phases.matchingLook"),
      t("phases.generatingPersonality"),
      t("phases.creatingAvatar"),
      t("phases.bringingToLife"),
    ],
    [t]
  )
  const validationMessages = useMemo<Record<string, string>>(
    () => ({
      style: t("validation.style"),
      ethnicity: t("validation.ethnicity"),
      eyeColor: t("validation.eyeColor"),
      hairColor: t("validation.hairColor"),
      hairStyle: t("validation.hairStyle"),
      bodyType: t("validation.bodyType"),
      breastSize: t("validation.breastSize"),
      buttSize: t("validation.buttSize"),
      name: t("validation.name"),
      voiceId: t("validation.voiceId"),
    }),
    [t]
  )
  const {
    draft,
    updateDraft,
    currentStep,
    maxVisitedStep,
    setStep,
    resetDraft,
    restoreSession,
  } = useCharacterStore()
  const router = useLocalizedRouter()
  const createMutation = useCreateCharacter()
  const { canUse: canUseCredits } = useCoins()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const isPreview =
    mode === "preview" ||
    user?.id === "workbench-user" ||
    isDevScenarioActive()
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const setPaywallContext = useUIStore((s) => s.setPaywallContext)

  const [direction, setDirection] = useState(1)
  const [isCreating, setIsCreating] = useState(false)
  const [creationPhase, setCreationPhase] = useState(0)
  const [invalidFields, setInvalidFields] = useState<string[]>([])
  const [createdCharacterId, setCreatedCharacterId] = useState<string | null>(null)
  const [finalizeTimedOut, setFinalizeTimedOut] = useState(false)
  const [finalizeRetryEpoch, setFinalizeRetryEpoch] = useState(0)
  const [simulatedProgress, setSimulatedProgress] = useState(0)
  const [showSignupCTA, setShowSignupCTA] = useState(false)
  const progressStartRef = useRef<number>(0)
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const generationInFlightRef = useRef(false)
  const sessionIdRef = useRef<string | null>(null)
  const pendingResumeGenerationRef = useRef(false)
  const [sessionHydrated, setSessionHydrated] = useState(false)
  const restoredFromSessionRef = useRef(false)

  const isGenerationScreen = isCreating || showSignupCTA || !!createdCharacterId

  const { data: createdCharacter, refetch: refetchCreatedCharacter } =
    useCharacter(createdCharacterId ?? "", {
      // Keep polling after the "taking longer" timeout: the avatar can land
      // after it (PLE-547), and polling stops on its own once isReady is true.
      pollUntilFound: !!createdCharacterId,
      isReady: isCreatedCharacterFinalized,
    })
  const createdCharacterFinalized = isCreatedCharacterFinalized(createdCharacter)
  const finalizedCreatedCharacter = createdCharacterFinalized ? createdCharacter : undefined

  const stepComplete = isCurrentStepComplete(draft, currentStep)

  useEffect(() => {
    if (!isPreview) return
    resetDraft()
    if (initialDraft) {
      updateDraft(initialDraft)
    }
    setStep(Math.max(0, Math.min(initialStep, SLIDE_COUNT - 1)))
    setCreatedCharacterId(null)
    if (initialStatus === "creating") {
      setIsCreating(true)
      setCreationPhase(1)
      setSimulatedProgress(68)
    } else {
      setIsCreating(false)
      setSimulatedProgress(0)
    }
  }, [isPreview, initialStep, initialDraft, initialStatus, resetDraft, setStep, updateDraft])

  // Clear individual invalid fields as the user fixes them (reactive, per-field)
  useEffect(() => {
    setInvalidFields((prev) => {
      if (prev.length === 0) return prev
      const next = prev.filter((field) => {
        switch (field) {
          case "style": return !draft.style
          case "ethnicity": return !draft.ethnicity
          case "eyeColor": return !draft.eyeColor
          case "hairColor": return !draft.hairColor
          case "hairStyle": return !draft.hairStyle
          case "bodyType": return !draft.bodyType
          case "breastSize": return !draft.breastSize
          case "buttSize": return !draft.buttSize
          case "name": return !draft.name || draft.name.trim().length < 2
          case "voiceId": return !draft.voiceId
          default: return false
        }
      })
      return next.length === prev.length ? prev : next
    })
  }, [
    draft.style, draft.ethnicity, draft.eyeColor, draft.hairColor, draft.hairStyle,
    draft.bodyType, draft.breastSize, draft.buttSize,
    draft.name, draft.description, draft.voiceId,
  ])

  // Restore a saved create session (survives reload, OAuth return, leaving /create).
  useEffect(() => {
    if (isPreview) {
      setSessionHydrated(true)
      return
    }

    const saved = loadCreateWizardSession()
    if (saved && shouldRestoreCreateSession(saved)) {
      restoredFromSessionRef.current = true
      sessionIdRef.current = saved.sessionId
      restoreSession(saved.draft, saved.currentStep, saved.maxVisitedStep)

      const onGenerationScreen =
        saved.phase === "generating" ||
        saved.phase === "signup_gate" ||
        saved.showSignupCTA

      if (onGenerationScreen) {
        setStep(REVIEW_STEP)
      }

      if (saved.showSignupCTA || saved.phase === "signup_gate") {
        setShowSignupCTA(true)
        setIsCreating(false)
      }

      if (saved.pendingResumeGeneration) {
        pendingResumeGenerationRef.current = true
      }
    } else if (saved) {
      clearCreateWizardSession()
    } else {
      resetDraft()
    }

    setSessionHydrated(true)
  }, [isPreview, restoreSession, resetDraft, setStep])

  const initDefaultsDoneRef = useRef(false)

  // Auto-set gender, seed random defaults (except style), and track creation start
  useEffect(() => {
    if (!sessionHydrated || isPreview || initDefaultsDoneRef.current) return
    initDefaultsDoneRef.current = true

    const currentDraft = useCharacterStore.getState().draft

    if (!currentDraft.gender) {
      updateDraft({ gender: "female" })
    }

    if (restoredFromSessionRef.current) {
      const missing = fillMissingRandomDefaults(
        useCharacterStore.getState().draft
      )
      if (Object.keys(missing).length > 0) {
        updateDraft(missing)
      }
    } else if (isPristineCharacterDraft(currentDraft)) {
      updateDraft(buildRandomCharacterDefaults())
      track("character:creation_start", {})
    }
  }, [sessionHydrated, isPreview, updateDraft])

  // Track each wizard step becoming visible so we can localize where users
  // abandon (step_complete alone only shows successful advances, not the drop).
  useEffect(() => {
    if (isPreview || !sessionHydrated || isGenerationScreen) return
    track("character:step_view", {
      step: currentStep,
      step_name: STEP_NAMES[currentStep] ?? `step_${currentStep}`,
    })
  }, [currentStep, isPreview, sessionHydrated, isGenerationScreen])

  const handleBringToLife = useCallback(() => {
    if (!createdCharacterId || !createdCharacterFinalized) return
    const chatId = createdCharacterId
    clearCreateWizardSession()
    resetDraft()
    router.push(`/chat/${chatId}`)
  }, [createdCharacterFinalized, createdCharacterId, resetDraft, router])

  // Persist wizard progress on every meaningful change.
  useEffect(() => {
    if (!sessionHydrated || isPreview || createdCharacterId) return
    if (isPristineCharacterDraft(draft) && !showSignupCTA) {
      return
    }

    if (!sessionIdRef.current) {
      sessionIdRef.current = crypto.randomUUID()
    }

    const phase: CreateWizardSession["phase"] = createdCharacterId
      ? "created"
      : isCreating
        ? "generating"
        : showSignupCTA
          ? "signup_gate"
          : "form"

    saveCreateWizardSession({
      version: 2,
      sessionId: sessionIdRef.current,
      savedAt: Date.now(),
      draft,
      currentStep,
      maxVisitedStep,
      phase,
      showSignupCTA,
      createdCharacterId,
      pendingResumeGeneration: showSignupCTA && !isAuthenticated,
    })
  }, [
    sessionHydrated,
    isPreview,
    draft,
    currentStep,
    maxVisitedStep,
    isCreating,
    showSignupCTA,
    createdCharacterId,
    isAuthenticated,
  ])

  // Cycle through creation phase messages
  useEffect(() => {
    if (!isCreating) return
    const interval = setInterval(() => {
      setCreationPhase((prev) => (prev + 1) % creationPhases.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [creationPhases.length, isCreating])

  useEffect(() => {
    if (!createdCharacterId || createdCharacterFinalized) {
      setFinalizeTimedOut(false)
      return
    }

    setFinalizeTimedOut(false)
    const timer = setTimeout(() => {
      setFinalizeTimedOut(true)
    }, CHARACTER_FINALIZE_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [createdCharacterId, createdCharacterFinalized, finalizeRetryEpoch])

  const handleRetryFinalize = useCallback(() => {
    setFinalizeTimedOut(false)
    setFinalizeRetryEpoch((epoch) => epoch + 1)
    void refetchCreatedCharacter()
  }, [refetchCreatedCharacter])

  // Simulated progress ticker
  useEffect(() => {
    if (isCreating) {
      progressStartRef.current = Date.now()
      setSimulatedProgress(0)
      progressTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - progressStartRef.current
        setSimulatedProgress(Math.round(getSimulatedProgress(elapsed)))
      }, 200)
    } else if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current)
    }
  }, [isCreating])

  // ─── Navigation ──────────────────────────────────────────────────────

  const scrollToTop = useCallback(() => {
    // Mobile: body scrolls. Desktop: <main> scrolls.
    window.scrollTo({ top: 0, behavior: "instant" })
    const main = document.querySelector("main")
    if (main) main.scrollTop = 0
  }, [])

  const goTo = useCallback(
    (i: number) => {
      if (i === currentStep) return
      setInvalidFields([])
      setDirection(i > currentStep ? 1 : -1)
      setStep(i)
      scrollToTop()
    },
    [currentStep, setStep, scrollToTop]
  )

  const goBack = useCallback(() => {
    if (currentStep === 0) return
    goTo(currentStep - 1)
  }, [currentStep, goTo])

  const validateAndNext = useCallback(() => {
    const missing: string[] = []
    // Step 0: Basics (style only)
    if (currentStep === 0) {
      if (!draft.style) missing.push("style")
    }
    // Step 1: Ethnicity
    if (currentStep === 1) {
      if (!draft.ethnicity) missing.push("ethnicity")
    }
    // Step 2: Hair & Eyes
    if (currentStep === 2) {
      if (!draft.eyeColor) missing.push("eyeColor")
      if (!draft.hairColor) missing.push("hairColor")
      if (!draft.hairStyle) missing.push("hairStyle")
    }
    // Step 3: Body
    if (currentStep === 3) {
      if (!draft.bodyType) missing.push("bodyType")
      if (["female", "trans"].includes(draft.gender || "female")) {
        if (!draft.breastSize) missing.push("breastSize")
        if (!draft.buttSize) missing.push("buttSize")
      }
    }
    // Step 4: Personality
    if (currentStep === 4) {
      if (!draft.name || draft.name.trim().length < 2) missing.push("name")
      if (!draft.voiceId) missing.push("voiceId")
    }

    if (missing.length > 0) {
      setInvalidFields(missing)
      toast.error(validationMessages[missing[0]] ?? t("validation.required"))
      return
    }
    track("character:step_complete", {
      step: currentStep,
      step_name: STEP_NAMES[currentStep] ?? `step_${currentStep}`,
    })

    setInvalidFields([])
    setDirection(1)
    setStep(Math.min(currentStep + 1, SLIDE_COUNT - 1))
    scrollToTop()
  }, [currentStep, draft, setStep, scrollToTop, t, validationMessages])

  // ─── Create handler ──────────────────────────────────────────────────

  const abortGenerationGate = useCallback(() => {
    setIsCreating(false)
    scrollToTop()
  }, [scrollToTop])

  const preGateFreeUserMessageLimit = useCallback(async () => {
    const userIsPaid = isPaidTier(user?.subscription ?? "free")
    if (isPreview || !isAuthenticated || userIsPaid) {
      return false
    }

    try {
      const messageLimit = await checkFreeUserLimits("message")
      const shouldBlock = shouldPreGateCharacterCreationForMessageLimit({
        isAuthenticated,
        isPaid: userIsPaid,
        limitResult: messageLimit.success
          ? (messageLimit.limitResult ?? null)
          : null,
      })

      if (!shouldBlock) return false

      track("character:creation_blocked_message_limit", {
        trigger: CHARACTER_CREATION_MESSAGE_LIMIT_TRIGGER,
        intent: "character_creation",
        remaining_messages_bucket: "0",
      })
      setShowSignupCTA(false)
      pendingResumeGenerationRef.current = false
      setPaywallContext({
        intent: "character_creation",
        trigger: CHARACTER_CREATION_MESSAGE_LIMIT_TRIGGER,
        characterId: null,
        characterName: draft.name || null,
        characterAvatar: null,
        messagesSentCount: null,
      })
      setActiveModal("subscribe-paywall")
      return true
    } catch {
      return false
    }
  }, [
    draft.name,
    isAuthenticated,
    isPreview,
    setActiveModal,
    setPaywallContext,
    user?.subscription,
  ])

  const runGenerationPipeline = useCallback(async () => {
    let didSucceed = false
    try {
      await waitForGenerationTease()

      if (isPreview) {
        if (!isAuthenticated) {
          // Show the inline signup CTA on the review screen instead of force-
          // opening the register modal on top of it (the dead-click wall).
          setIsCreating(false)
          setShowSignupCTA(true)
          pendingResumeGenerationRef.current = true
          track("character:creation_blocked_unauthenticated", {
            gate: "preview_generation",
          })
          return
        }

        const tier = user?.subscription ?? "free"
        if (!isPaidTier(tier)) {
          setPaywallContext({
            intent: "character_creation",
            trigger: "preview_character_creation_limit",
            characterId: null,
            characterName: draft.name || "your character",
            characterAvatar: null,
            messagesSentCount: null,
          })
          abortGenerationGate()
          setActiveModal("subscribe-paywall")
          return
        }

        if (previewCoinBalance < COIN_COSTS.character_creation) {
          abortGenerationGate()
          setActiveModal("buy-credits-paywall")
          return
        }

        setSimulatedProgress(72)
        toast.success("Preview: character creation simulated.")
        didSucceed = true
        return
      }

      if (!isAuthenticated) {
        // Inline signup CTA (the showSignupCTA review screen) carries the gate;
        // the user opts into the register modal via its explicit CTA button.
        setIsCreating(false)
        setShowSignupCTA(true)
        pendingResumeGenerationRef.current = true
        track("character:creation_blocked_unauthenticated", {
          gate: "generation",
        })
        return
      }

      setPaywallContext({
        intent: "character_creation",
        trigger: "character_creation_limit",
        characterId: null,
        characterName: draft.name || null,
        characterAvatar: null,
        messagesSentCount: null,
      })
      const creditCheck = await canUseCredits("character_creation")
      if (!creditCheck.allowed) {
        abortGenerationGate()
        return
      }

      const finalDraft = buildFinalDraft(draft)
      const result = characterSchema.safeParse(finalDraft)
      if (!result.success) {
        abortGenerationGate()
        toast.error(result.error.issues[0].message)
        return
      }

      const res = await createMutation
        .mutateAsync({
          name: finalDraft.name,
          description: finalDraft.description,
          gender: finalDraft.gender as "female" | "male" | "trans",
          age: finalDraft.age,
          ethnicity: finalDraft.ethnicity,
          hairStyle: finalDraft.hairStyle,
          hairColor: finalDraft.hairColor,
          eyeColor: finalDraft.eyeColor,
          bodyType: finalDraft.bodyType,
          breastSize: finalDraft.breastSize || undefined,
          buttSize: finalDraft.buttSize || undefined,
          personality: finalDraft.personality as string[],
          style: finalDraft.style as "realistic" | "anime",
          backstory: finalDraft.backstory || "",
          interests: finalDraft.interests,
          communicationStyle: finalDraft.communicationStyle,
          dominanceLevel: finalDraft.dominanceLevel,
          isPublic: finalDraft.isPublic ?? true,
          skinTone: finalDraft.skinTone || undefined,
          occupation: finalDraft.occupation || undefined,
          relationshipType: finalDraft.relationshipType || undefined,
          hobby: finalDraft.hobby || undefined,
          fetish: finalDraft.fetish || undefined,
          voiceId: finalDraft.voiceId || undefined,
          greeting: finalDraft.greeting || undefined,
          scenario: finalDraft.scenario || undefined,
          customPhysicalDetails: finalDraft.customPhysicalDetails || undefined,
          additionalPersonalityDetails:
            finalDraft.additionalPersonalityDetails || undefined,
          extraDetails: finalDraft.extraDetails || undefined,
        })
        .catch((error) => {
          abortGenerationGate()
          toast.error(
            error instanceof Error && error.message
              ? error.message
              : "Something went wrong. Please try again."
          )
          return null
        })

      if (!res) return

      if (res.success && res.characterId) {
        track("character:creation_complete", {
          style: finalDraft.style as string,
          gender: finalDraft.gender,
          has_custom_backstory:
            !!finalDraft.backstory &&
            finalDraft.backstory !== CREATION_DEFAULTS.backstory,
        })
        setCreatedCharacterId(res.characterId)
        setIsCreating(false)
        setShowSignupCTA(false)
        pendingResumeGenerationRef.current = false
        setSimulatedProgress(100)
        scrollToTop()
        toast.success("Character created successfully!")
        didSucceed = true
      } else {
        abortGenerationGate()
        toast.error(res.error || "Failed to create character")
      }
    } catch (error) {
      abortGenerationGate()
      captureClientException(error, {
        scope: "character-creation",
        extra: { source: "create" },
      })
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : "Something went wrong. Please try again."
      )
    } finally {
      if (!didSucceed) setIsCreating(false)
      generationInFlightRef.current = false
    }
  }, [
    draft,
    createMutation,
    isAuthenticated,
    setActiveModal,
    setPaywallContext,
    canUseCredits,
    scrollToTop,
    isPreview,
    user?.subscription,
    previewCoinBalance,
    abortGenerationGate,
  ])

  const startGeneration = useCallback(() => {
    if (generationInFlightRef.current) return
    generationInFlightRef.current = true
    void (async () => {
      const blockedByMessageLimit = await preGateFreeUserMessageLimit()
      if (blockedByMessageLimit) {
        generationInFlightRef.current = false
        return
      }

      setShowSignupCTA(false)
      setIsCreating(true)
      setCreationPhase(0)
      void runGenerationPipeline()
    })().catch(() => {
      generationInFlightRef.current = false
    })
  }, [preGateFreeUserMessageLimit, runGenerationPipeline])

  const handleSignupCTA = useCallback(() => {
    setActiveModal("register")
  }, [setActiveModal])

  const handleCreate = useCallback(() => {
    startGeneration()
  }, [startGeneration])

  // After signup, auto-resume character generation without another Generate click.
  useEffect(() => {
    if (!sessionHydrated || isPreview || !isAuthenticated) return
    if (!pendingResumeGenerationRef.current) return
    if (generationInFlightRef.current || isCreating || createdCharacterId) return

    pendingResumeGenerationRef.current = false
    startGeneration()
  }, [
    sessionHydrated,
    isPreview,
    isAuthenticated,
    isCreating,
    createdCharacterId,
    startGeneration,
  ])

  const handleGenerate = useCallback(() => {
    const missing: string[] = []
    if (!draft.name || draft.name.trim().length < 2) missing.push("name")
    if (!draft.voiceId) missing.push("voiceId")

    if (missing.length > 0) {
      setInvalidFields(missing)
      toast.error(validationMessages[missing[0]] ?? t("validation.required"))
      return
    }

    track("character:step_complete", { step: PERSONALITY_STEP, step_name: "personality" })
    setInvalidFields([])
    setDirection(1)
    setStep(REVIEW_STEP)
    scrollToTop()
    startGeneration()
  }, [draft, setStep, scrollToTop, startGeneration, t, validationMessages])

  const isLastScreen = currentStep === SLIDE_COUNT - 1

  const isFinalizingCreatedCharacter =
    !!createdCharacterId && !createdCharacterFinalized && !finalizeTimedOut
  const finalizeStalled =
    !!createdCharacterId && !createdCharacterFinalized && finalizeTimedOut
  const isCharacterCreated = !!createdCharacterId && createdCharacterFinalized
  const showBack =
    !createdCharacterId && !isCreating && !showSignupCTA && currentStep > 0

  return (
    <div className="relative w-full max-w-4xl mx-auto">
      {/* Sticky header: title + step indicator — stays visible while scrolling */}
      {!isGenerationScreen ? (
        <div className="sticky top-14 sm:top-16 z-20 backdrop-blur-md -mx-2 px-2 md:-mx-6 md:px-6 pt-2 pb-4 border-b border-border/10">
          <h1 className="heading-serif text-center text-xl sm:text-3xl text-foreground">
            {t("title")}
          </h1>
          <div className="mt-3">
            <SlideIndicator current={currentStep} maxVisitedStep={maxVisitedStep} onGoTo={goTo} />
          </div>
        </div>
      ) : isCharacterCreated ? null : (
        <h1 className="heading-serif text-center text-xl sm:text-3xl text-foreground mb-6">
          {isFinalizingCreatedCharacter
            ? t("status.finalizing")
            : finalizeStalled
              ? t("status.almostReady")
            : showSignupCTA
              ? t("status.almostThere")
              : t("status.creating")}
        </h1>
      )}

      {/* Form Panel */}
      <div className="flex-1 min-w-0">
          {/* Screen Content */}
          <div
            className={cn(
              "relative min-h-[min(350px,46dvh)] sm:min-h-[min(400px,52dvh)] pb-4 sm:pb-8",
              isCharacterCreated ? "pt-0" : "pt-4"
            )}
          >
            <AnimatePresence mode="popLayout" custom={direction} initial={false}>
              <motion.div
                key={isGenerationScreen ? "creation-state" : currentStep}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 320, damping: 34, mass: 0.9 },
                  scale: { type: "spring", stiffness: 320, damping: 34, mass: 0.9 },
                  opacity: { duration: 0.2, ease: "easeOut" },
                }}
                className="w-full"
              >
                {isGenerationScreen ? (
                  <ReviewStep
                    isCreating={isCreating || isFinalizingCreatedCharacter}
                    showSignupCTA={showSignupCTA}
                    onSignupClick={handleSignupCTA}
                    onChatClick={isCharacterCreated ? handleBringToLife : undefined}
                    simulatedProgress={simulatedProgress}
                    creationPhase={creationPhases[creationPhase]}
                    createdCharacter={finalizedCreatedCharacter}
                    finalizeStalled={finalizeStalled}
                    onRetryFinalize={handleRetryFinalize}
                  />
                ) : (
                  <>
                    {currentStep === 0 && <BasicsStep invalidFields={invalidFields} />}
                    {currentStep === 1 && <EthnicityStep invalidFields={invalidFields} />}
                    {currentStep === 2 && <HairEyesStep invalidFields={invalidFields} />}
                    {currentStep === 3 && <BodyStep invalidFields={invalidFields} />}
                    {currentStep === 4 && <PersonalityStep invalidFields={invalidFields} />}
                    {currentStep === 5 && (
                      <ReviewStep
                        isCreating={false}
                        simulatedProgress={0}
                        creationPhase=""
                      />
                    )}
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation — bottom sticky on mobile */}
          {!isGenerationScreen && (
            <div
              className={cn(
                "flex items-center justify-center",
                "fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border/20 px-3 py-2.5",
                "sm:sticky sm:bottom-0 sm:z-30 sm:border-0 sm:px-0 sm:pt-7 sm:pb-4 sm:backdrop-blur-none sm:bg-gradient-to-t sm:from-background sm:via-background/85 sm:to-transparent"
              )}
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            >
              <div className="flex w-full items-center justify-center gap-2 sm:w-auto sm:gap-3">
              {showBack && (
                <button
                  type="button"
                  onClick={goBack}
                  aria-label={t("previousStep")}
                  className="flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-border/40 bg-transparent px-4 py-2.5 text-sm font-semibold text-foreground/80 transition-colors hover:bg-foreground/[0.06] min-h-11 sm:gap-2 sm:px-6 sm:py-3.5 sm:text-base sm:min-h-[48px]"
                >
                  <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
                  {t("back")}
                </button>
              )}
              {isCharacterCreated ? null : showSignupCTA ? null : currentStep === PERSONALITY_STEP ? (
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={isCreating}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all disabled:opacity-50 min-h-11 sm:px-8 sm:py-3.5 sm:text-base sm:min-h-[48px]",
                    showBack ? "flex-1 sm:flex-initial sm:w-auto" : "w-full sm:w-auto",
                    stepComplete
                      ? "bg-accent-blue text-white hover:bg-accent-blue/90"
                      : "bg-muted/80 text-muted-foreground hover:bg-muted hover:opacity-90 font-semibold"
                  )}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {t("generating")}
                    </>
                  ) : (
                    <>
                      <Icon name="flash" size={16} className="size-4" />
                      {t("generate")}
                      {isPaidTier(user?.subscription ?? "free") && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold tabular-nums">
                          <Coins className="size-3" />
                          {COIN_COSTS.character_creation}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ) : isLastScreen ? (
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={isCreating}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-full bg-accent-blue px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-accent-blue/90 disabled:opacity-50 min-h-11 sm:px-8 sm:py-3.5 sm:text-base sm:min-h-[48px]",
                    showBack ? "flex-1 sm:flex-initial sm:w-auto" : "w-full sm:w-auto"
                  )}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {t("generating")}
                    </>
                  ) : (
                    <>
                      <Icon name="flash" size={16} className="size-4" />
                      {t("generate")}
                      {isPaidTier(user?.subscription ?? "free") && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold tabular-nums">
                          <Coins className="size-3" />
                          {COIN_COSTS.character_creation}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={validateAndNext}
                  disabled={isCreating || !stepComplete}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-all min-h-11 sm:gap-2 sm:px-8 sm:py-3.5 sm:text-base sm:min-h-[48px]",
                    showBack ? "flex-1 sm:flex-initial sm:w-auto" : "w-full sm:w-auto",
                    stepComplete
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-muted/80 text-muted-foreground hover:bg-muted hover:opacity-90"
                  )}
                >
                  {t("next")}
                  <svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
                </button>
              )}
              </div>
            </div>
          )}

          {showSignupCTA && !isCharacterCreated && (
            <div
              className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-center border-t border-border/20 bg-background/95 px-3 py-2.5 backdrop-blur-md sm:hidden"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            >
              <button
                type="button"
                onClick={handleSignupCTA}
                className="group relative flex min-h-11 w-full items-center justify-center overflow-hidden rounded-full border border-white/18 bg-accent-blue px-4 py-2.5 text-sm font-extrabold text-white shadow-[0_12px_34px_rgba(29,125,242,0.36),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-12px_22px_rgba(0,24,84,0.24)] transition-all hover:brightness-110 active:scale-[0.98]"
              >
                <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0.08)_38%,rgba(0,0,0,0.18)_100%)] opacity-95 transition-opacity group-hover:opacity-100" />
                <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-white/60" />
                <span className="relative z-10">{t("unlockCreateFree")}</span>
              </button>
            </div>
          )}

          {isCharacterCreated && createdCharacterId && (
            <div
              className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-center border-t border-border/20 bg-background/95 px-3 py-2.5 backdrop-blur-md sm:hidden"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            >
              <button
                type="button"
                onClick={handleBringToLife}
                className="group relative flex min-h-11 w-full items-center justify-center overflow-hidden rounded-full border border-white/18 bg-accent-blue px-4 py-2.5 text-sm font-extrabold text-white shadow-[0_12px_34px_rgba(29,125,242,0.36),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-12px_22px_rgba(0,24,84,0.24)] transition-all hover:brightness-110 active:scale-[0.98]"
              >
                <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.26)_0%,rgba(255,255,255,0.08)_38%,rgba(0,0,0,0.18)_100%)] opacity-95 transition-opacity group-hover:opacity-100" />
                <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-white/60" />
                <span className="relative z-10">{getBringToLifeLabel(createdCharacter?.gender ?? draft.gender)}</span>
              </button>
            </div>
          )}

          {/* Spacer for fixed bottom button on mobile */}
          {(!isGenerationScreen || showSignupCTA || isCharacterCreated) && (
            <div className="h-20 sm:h-0" />
          )}
        </div>
    </div>
  )
}
